import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { colors, typography } from "../../design/tokens";

interface MarkdownContentProps {
  content: string;
  className?: string;
}

/**
 * Reusable markdown content renderer with consistent styling across all text pages.
 * Used by: Blog posts, Privacy Policy, Terms of Service, and other markdown-based pages.
 *
 * Features:
 * - Custom component overrides for all markdown elements
 * - Design token integration for consistent styling
 * - Proper spacing and typography hierarchy
 * - No dependency on @tailwindcss/typography plugin
 */
export function MarkdownContent({ content, className = "" }: MarkdownContentProps) {
  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => (
            <p
              className={`${typography.size.base} leading-[1.6] mb-6`}
              style={{ color: colors.hex.textSecondary }}
            >
              {children}
            </p>
          ),
          h1: ({ children }) => (
            <h1
              className={`${typography.size.xl} ${typography.weight.semibold} leading-[1.3] mt-10 mb-4 first:mt-0`}
              style={{ color: colors.hex.white }}
            >
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2
              className={`${typography.size.lg} ${typography.weight.semibold} leading-[1.4] mt-8 mb-3`}
              style={{ color: colors.hex.white }}
            >
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3
              className={`${typography.size.base} ${typography.weight.semibold} leading-[1.4] mt-6 mb-2`}
              style={{ color: colors.hex.white }}
            >
              {children}
            </h3>
          ),
          ul: ({ children }) => (
            <ul
              className="my-6 space-y-1.5 list-disc pl-6"
              style={{ color: colors.hex.textSecondary }}
            >
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol
              className="my-6 space-y-1.5 list-decimal pl-6"
              style={{ color: colors.hex.textSecondary }}
            >
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className={`${typography.size.base} leading-[1.6]`}>
              {children}
            </li>
          ),
          a: ({ children, href }) => (
            <a
              href={href}
              className="no-underline hover:underline transition-all duration-200"
              style={{ color: colors.primary.blueAlt }}
            >
              {children}
            </a>
          ),
          strong: ({ children }) => (
            <strong
              className={typography.weight.semibold}
              style={{ color: colors.hex.white }}
            >
              {children}
            </strong>
          ),
          code: ({ children }: any) => (
            <code className="bg-gray-800 px-1.5 py-0.5 rounded text-sm">
              {children}
            </code>
          ),
          table: ({ children }) => (
            <div className="my-6 overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead
              className="border-b"
              style={{ borderColor: colors.hex.borderPrimary }}
            >
              {children}
            </thead>
          ),
          tbody: ({ children }) => <tbody>{children}</tbody>,
          tr: ({ children }) => (
            <tr
              className="border-b last:border-b-0"
              style={{ borderColor: colors.hex.borderPrimary }}
            >
              {children}
            </tr>
          ),
          th: ({ children }) => (
            <th
              className={`${typography.weight.semibold} text-left px-4 py-3`}
              style={{ color: colors.hex.white }}
            >
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td
              className="px-4 py-3"
              style={{ color: colors.hex.textSecondary }}
            >
              {children}
            </td>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
